// Messages — DM inbox + thread view + inline LiveKit video call.
import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { MessageCircle, Send, Video, VideoOff, ArrowLeft, Users, Sparkles, Camera as CameraIcon, Phone, PhoneOff, Mic, MicOff, Paperclip, Film, Trash2, RefreshCw } from "lucide-react";
import { LiveKitRoom, ParticipantTile, GridLayout, useTracks, useRemoteParticipants, useLocalParticipant, useConnectionState, RoomAudioRenderer } from "@livekit/components-react";
import { Track } from "livekit-client";
import "@livekit/components-styles";
import { toast, Toaster } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import LiveKitStreamRoom from "../components/LiveKitStreamRoom";
import SnapComposer from "../components/SnapComposer";
import { getRingContext } from "../lib/ringAudio";

export function DMInbox() {
  const [threads, setThreads] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const nav = useNavigate();
  useEffect(() => {
    Promise.all([
      api.get("/messenger/threads").then(({ data }) => setThreads(data.items || [])).catch(() => {}),
      api.get("/friends").then(({ data }) => setFriends(data.friends || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);
  const openWithFriend = async (fid) => {
    try { const { data } = await api.post("/messenger/threads", { member_ids: [fid] }); nav(`/dm/${data.thread.id}`); }
    catch { toast.error("Could not open chat"); }
  };
  return (
    <div className="profile-light max-w-3xl mx-auto py-6 px-4 space-y-4" data-testid="dm-inbox">
      <Toaster theme="dark" position="top-center" />
      <div className="flex items-center justify-between">
        <h1 className="font-display font-black text-3xl">Messages</h1>
        <div className="flex items-center gap-4">
          {friends.length >= 2 && (
            <button onClick={() => setShowGroupModal(true)} className="text-[11px] tracking-widest uppercase text-nexus-magenta hover:underline" data-testid="dm-inbox-new-group">+ New group</button>
          )}
          <Link to="/friends" className="text-[11px] tracking-widest uppercase text-nexus-magenta hover:underline" data-testid="dm-inbox-friends-link">All friends →</Link>
        </div>
      </div>
      {showGroupModal && (
        <NewGroupModal
          friends={friends}
          onClose={() => setShowGroupModal(false)}
          onCreated={(threadId) => { setShowGroupModal(false); nav(`/dm/${threadId}`); }}
        />
      )}
      {friends.length > 0 && (
        <section className="space-y-2" data-testid="dm-inbox-friends">
          <div className="text-[10px] tracking-widest uppercase text-black/40">// friends · tap to chat</div>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {friends.map((f) => (
              <button
                key={f.id}
                onClick={() => openWithFriend(f.id)}
                data-testid={`dm-friend-${f.id}`}
                className="flex flex-col items-center gap-1 min-w-[64px] hover:opacity-90"
                title={`Message ${f.display_name}`}
              >
                <img src={f.avatar} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-white shadow" />
                <span className="text-[10px] text-black/70 truncate max-w-[64px]">{f.display_name}</span>
              </button>
            ))}
          </div>
        </section>
      )}
      {loading ? <div className="text-black/40">Loading…</div>
       : threads.length === 0 ? <div className="rounded-2xl bg-white border border-black/10 p-6 text-black/50 text-sm">No conversations yet. Open a friend&apos;s profile and hit <strong>Message</strong>.</div>
       : (
        <div className="rounded-2xl bg-white border border-black/10 divide-y divide-black/5">
          {threads.map((t) => {
            const others = (t.members || []).filter((m) => m.id !== null);
            const label = t.is_group ? (t.title || (others.map((o) => o.display_name).join(", "))) : (others[0]?.display_name || "Chat");
            const avatar = t.is_group ? null : others[0]?.avatar;
            return (
              <Link key={t.id} to={`/dm/${t.id}`} className="flex items-center gap-3 p-3 hover:bg-black/5" data-testid={`thread-${t.id}`}>
                {avatar ? <img src={avatar} alt="" className="w-11 h-11 rounded-full object-cover" /> : <div className="w-11 h-11 rounded-full bg-black/10 flex items-center justify-center"><Users className="w-5 h-5 text-black/60" /></div>}
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm truncate">{label}</div>
                  <div className="text-xs text-black/60 truncate">{t.last_message || "New conversation"}</div>
                </div>
                <div className="text-[10px] text-black/40">{new Date(t.last_ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

// NewGroupModal — pick 2+ friends and an optional name to start a group chat.
// Group video calling needs no separate plumbing: /calls/start already rings
// every id in the thread's member_ids (not just a single callee), and the big
// call stage (RemoteOnlyStage) already renders every remote participant via
// LiveKit's GridLayout — both were built generically, just never reachable
// because there was no UI to create a >2-person thread in the first place.
function NewGroupModal({ friends, onClose, onCreated }) {
  const [selected, setSelected] = useState([]);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const toggle = (fid) => setSelected((cur) => cur.includes(fid) ? cur.filter((x) => x !== fid) : [...cur, fid]);
  const create = async () => {
    if (selected.length < 2) { toast.error("Pick at least 2 friends for a group"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/messenger/threads", { member_ids: selected, title: title.trim() || undefined });
      onCreated(data.thread.id);
    } catch { toast.error("Could not create group"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="new-group-modal">
      <div className="bg-white rounded-2xl w-full max-w-sm p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="font-display font-bold text-lg">New group chat</div>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value.slice(0, 60))}
          placeholder="Group name (optional)"
          className="w-full border border-black/15 rounded-md px-3 py-2 text-sm"
          data-testid="new-group-title"
        />
        <div className="max-h-64 overflow-y-auto space-y-1">
          {friends.map((f) => {
            const on = selected.includes(f.id);
            return (
              <button
                key={f.id}
                onClick={() => toggle(f.id)}
                className={`w-full flex items-center gap-2 p-2 rounded-lg text-left ${on ? "bg-nexus-magenta/10" : "hover:bg-black/5"}`}
                data-testid={`new-group-friend-${f.id}`}
              >
                <img src={f.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                <span className="text-sm flex-1">{f.display_name}</span>
                {on && <span className="text-nexus-magenta text-xs">✓</span>}
              </button>
            );
          })}
        </div>
        <div className="text-[11px] text-black/40">{selected.length} selected · need 2+</div>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 rounded-md border border-black/15 text-sm">Cancel</button>
          <button onClick={create} disabled={busy || selected.length < 2} className="flex-1 py-2 rounded-md bg-nexus-magenta text-white text-sm disabled:opacity-40" data-testid="new-group-create">
            {busy ? "Creating…" : "Create group"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function DMThread() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const [inCall, setInCall] = useState(params.get("call") === "1");
  const [filter, setFilter] = useState("none");   // Snapchat-lite CSS filters
  const [snapOpen, setSnapOpen] = useState(false);
  const scrollRef = useRef(null);

  // Basic filters — applied via CSS `filter:` on the LiveKit <video> subtree.
  const FILTERS = [
    { id: "none",       label: "None",     css: "none" },
    { id: "grayscale",  label: "B&W",      css: "grayscale(1) contrast(1.05)" },
    { id: "sepia",      label: "Sepia",    css: "sepia(0.9)" },
    { id: "cool",       label: "Cool",     css: "hue-rotate(210deg) saturate(1.2)" },
    { id: "warm",       label: "Warm",     css: "hue-rotate(-30deg) saturate(1.3) brightness(1.05)" },
    { id: "blur",       label: "Blur BG",  css: "blur(1.5px) contrast(1.1)" },
    { id: "vhs",        label: "VHS",      css: "contrast(1.35) saturate(1.4) hue-rotate(-8deg) brightness(0.98)" },
    { id: "neon",       label: "Neon",     css: "saturate(2) contrast(1.2) hue-rotate(280deg)" },
  ];

  const load = () => api.get(`/messenger/threads/${id}/messages`).then(({ data }) => setData(data)).catch(() => nav("/dm"));
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [data?.messages?.length]);

  if (!data || !user) return <div className="text-white/40 text-center py-10">Loading…</div>;
  const others = (data.thread.members || []).filter((m) => m.id !== user.id);
  const label = data.thread.is_group ? (data.thread.title || others.map((o) => o.display_name).join(", ")) : (others[0]?.display_name || "Chat");
  const avatar = data.thread.is_group ? null : others[0]?.avatar;

  const send = async () => {
    if (!msg.trim()) return;
    try {
      const m = await api.post("/messenger/send", { thread_id: id, text: msg });
      setData((d) => ({ ...d, messages: [...d.messages, m.data] }));
      setMsg("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Blocked"); }
  };

  const sendSnap = async (dataUrl, filterId, caption) => {
    try {
      const m = await api.post("/messenger/send", {
        thread_id: id,
        text: caption ? `📸 ${caption}` : "📸 Snap",
        media_url: dataUrl,
        media_kind: "image",
        meta: { snap_filter: filterId },
      });
      setData((d) => ({ ...d, messages: [...d.messages, m.data] }));
      setSnapOpen(false);
      toast.success("Snap sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Snap failed");
    }
  };

  return (
    <div className="profile-light max-w-3xl mx-auto py-4 px-4 h-[calc(100vh-100px)] flex flex-col" data-testid="dm-thread">
      <Toaster theme="dark" position="top-center" />
      <div className="flex items-center gap-3 border-b border-black/10 pb-3">
        <Link to="/dm" className="p-1.5 rounded-full hover:bg-black/5"><ArrowLeft className="w-4 h-4" /></Link>
        {avatar
          ? <Link to={`/u/${others[0]?.id}`} data-testid="dm-header-avatar"><img src={avatar} alt="" className="w-9 h-9 rounded-full object-cover hover:ring-2 hover:ring-nexus-cyan transition" /></Link>
          : <div className="w-9 h-9 rounded-full bg-black/10 flex items-center justify-center"><Users className="w-4 h-4" /></div>}
        <div className="flex-1 font-display font-bold">{label}</div>
        <button onClick={() => setSnapOpen(true)} data-testid="dm-open-snap" className="rounded-full px-3 py-1.5 text-[10px] font-display tracking-wider uppercase bg-gradient-to-r from-nexus-pink to-nexus-magenta text-white shadow" title="Send a snap">
          <CameraIcon className="w-3.5 h-3.5 inline mr-1" /> Snap
        </button>
        <button
          onClick={async () => {
            if (inCall) { setInCall(false); return; }
            // Fire /calls/start so the receiver's IncomingCallOverlay picks it up
            try { await api.post("/calls/start", { thread_id: id }); } catch {}
            setInCall(true);
          }}
          data-testid="dm-toggle-call"
          title={inCall ? "End call" : "Start video call"}
          className={`w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-105 ${inCall ? "bg-red-500 text-white" : "bg-green-500 text-white"}`}
        >
          {inCall ? <PhoneOff className="w-4 h-4" /> : <Phone className="w-4 h-4" />}
        </button>
      </div>

      {snapOpen && <SnapComposer onClose={() => setSnapOpen(false)} onSend={sendSnap} />}

      {inCall && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center p-0 sm:p-6"
          data-testid="dm-video-panel"
          role="dialog"
          aria-modal="true"
          aria-label="Video call"
        >
          <SnapchatCallView
            threadId={id}
            videoRoom={data.video_room}
            filter={filter}
            filters={FILTERS}
            remoteName={label}
            remoteAvatar={avatar}
            onEnd={() => setInCall(false)}
            onFilterChange={setFilter}
          />
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto py-3 space-y-2" data-testid="dm-messages">
        {data.messages.map((m) => {
          const mine = m.from_id === user.id;
          const canDelete = mine || user.role === "owner" || user.role === "staff";
          const onDelete = async () => {
            if (!window.confirm("Delete this message?")) return;
            try {
              await api.delete(`/dm/message/${m.id}`);
              setData((d) => ({ ...d, messages: d.messages.filter((x) => x.id !== m.id) }));
            } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
          };
          return (
            <div key={m.id} className={`group flex gap-2 items-start ${mine ? "justify-end" : ""}`}>
              {!mine && <img src={m.from_avatar} alt="" className="w-7 h-7 rounded-full object-cover" />}
              {mine && canDelete && (
                <button
                  onClick={onDelete}
                  className="opacity-0 group-hover:opacity-100 transition text-black/40 hover:text-nexus-crimson self-center"
                  title="Delete message"
                  aria-label="Delete message"
                  data-testid={`dm-delete-${m.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
              <div className={`max-w-[70%] px-3 py-2 rounded-2xl text-sm ${mine ? "bg-nexus-cyan text-black" : "bg-black/5 text-black"}`}>
                {!mine && <div className="text-[10px] font-display text-black/50 mb-0.5">{m.from_name}</div>}
                {m.media_url && m.media_kind === "image" && (
                  <img
                    src={resolveMediaUrl(m.media_url)}
                    alt=""
                    className="rounded-xl max-w-full mb-1"
                    data-testid={`msg-snap-${m.id}`}
                    onError={(e) => {
                      // If the image still won't load after the URL heal
                      // (e.g. file genuinely deleted), swap in a small
                      // inline placeholder instead of leaving a broken
                      // icon. This mirrors what LinkifiedText shows for
                      // plain-text media rows.
                      e.currentTarget.replaceWith(Object.assign(document.createElement("div"), {
                        className: "text-xs text-black/50 italic",
                        textContent: "🖼️ image unavailable",
                      }));
                    }}
                  />
                )}
                {m.media_url && m.media_kind === "video" && (
                  <video src={resolveMediaUrl(m.media_url)} controls className="rounded-xl max-w-full mb-1" data-testid={`msg-video-${m.id}`} />
                )}
                <LinkifiedText text={m.text} />
              </div>
              {!mine && canDelete && (
                <button
                  onClick={onDelete}
                  className="opacity-0 group-hover:opacity-100 transition text-black/40 hover:text-nexus-crimson self-center"
                  title="Delete message"
                  aria-label="Delete message"
                  data-testid={`dm-delete-${m.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 pt-2 border-t border-black/10 items-center">
        <AttachFileButton threadId={id} onSent={(newMsg) => setData((d) => ({ ...d, messages: [...d.messages, newMsg] }))} />
        <input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Type a message or paste a link…"
               className="flex-1 rounded-full bg-white border border-black/15 px-4 py-2 text-sm text-black focus:border-nexus-cyan focus:outline-none" data-testid="dm-input" />
        <button type="submit" className="rounded-full bg-nexus-cyan text-black w-10 h-10 flex items-center justify-center" data-testid="dm-send"><Send className="w-4 h-4" /></button>
      </form>
    </div>
  );
}

// AttachFileButton — paperclip button that opens a device file picker (images
// AND videos). Uploads to the existing /upload/image or /upload/video endpoint,
// then posts a DM message with the resulting URL.
function AttachFileButton({ threadId, onSent }) {
  const imgRef = useRef(null);
  const vidRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState(false);
  const upload = async (file, kind) => {
    setBusy(true); setMenu(false);
    try {
      const fd = new FormData(); fd.append("file", file);
      const endpoint = kind === "video" ? "/upload/video" : "/upload/image";
      const { data: up } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const abs = up.url.startsWith("http") ? up.url : `${backend}${up.url}`;
      const { data: msg } = await api.post("/messenger/send", {
        thread_id: threadId, text: "", media_url: abs, media_kind: kind,
      });
      onSent(msg);
      toast.success(kind === "video" ? "Video sent" : "Image sent");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setMenu((v) => !v)}
        disabled={busy}
        className="rounded-full bg-black/5 hover:bg-black/10 w-10 h-10 flex items-center justify-center disabled:opacity-40"
        data-testid="dm-attach"
        title="Attach photo or video"
      >
        <Paperclip className="w-4 h-4" />
      </button>
      {menu && (
        <div className="absolute bottom-12 left-0 bg-white border border-black/10 rounded-xl shadow-xl p-1.5 flex flex-col gap-1 z-10" data-testid="dm-attach-menu">
          <button type="button" onClick={() => imgRef.current?.click()} className="flex items-center gap-2 px-3 py-1.5 hover:bg-black/5 rounded text-xs" data-testid="dm-attach-image">
            <CameraIcon className="w-3.5 h-3.5" /> Photo
          </button>
          <button type="button" onClick={() => vidRef.current?.click()} className="flex items-center gap-2 px-3 py-1.5 hover:bg-black/5 rounded text-xs" data-testid="dm-attach-video">
            <Film className="w-3.5 h-3.5" /> Video
          </button>
        </div>
      )}
      <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f, "image"); e.target.value = ""; }} />
      <input ref={vidRef} type="file" accept="video/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f, "video"); e.target.value = ""; }} />
    </div>
  );
}

// resolveMediaUrl — heals legacy DM/upload URLs that were saved as
// `${BACKEND}/uploads/…`. The K8s ingress only routes `/api/*` to the
// backend, so a plain `/uploads/…` link resolves to the frontend SPA's
// index.html — meaning <img> silently fails to load and the browser
// falls back to showing the `alt` text (that's the "attachment" text
// that was appearing next to device-uploaded images).
// Backend now serves the same files under `/api/uploads/` too; this
// helper transparently rewrites older broken links so they display
// without needing a DB migration. Base64 (`data:image/...`) URLs and
// any already-correct `/api/uploads/*` URLs pass through unchanged.
function resolveMediaUrl(u) {
  if (!u || typeof u !== "string") return u;
  if (u.startsWith("data:")) return u;
  // Match both absolute and root-relative broken paths.
  // e.g. "https://host/uploads/foo.jpg"  →  "https://host/api/uploads/foo.jpg"
  //      "/uploads/foo.jpg"              →  "/api/uploads/foo.jpg"
  return u.replace(/(^|:\/\/[^/]+)\/uploads\//, "$1/api/uploads/");
}

// LinkifiedText — turns any http(s) URL in the message into a clickable link.
// Supports the common paste-from-TikTok/YouTube/etc. workflow.
function LinkifiedText({ text }) {
  if (!text) return null;
  const parts = text.split(/(https?:\/\/[^\s]+)/g);
  return (
    <>{parts.map((p, i) => {
      if (/^https?:\/\//.test(p)) {
        return <a key={i} href={p} target="_blank" rel="noopener noreferrer" className="underline text-blue-600 break-all" data-testid="dm-link">{p}</a>;
      }
      return <span key={i}>{p}</span>;
    })}</>
  );
}


// SnapchatCallView — Snapchat-style call UI:
//   • Big remote / self video (LiveKit) full-frame
//   • Small "you" PiP top-right showing your own camera via getUserMedia
//   • Ringing tone (Web Audio API) until LiveKit connects OR 8 s cap
//   • Transparent overlay controls: mic / cam / end-call (red phone)
//   • Filters bar tucked below on tablet+, hidden behind ⚡ toggle on mobile
//   • Aspect: 9:16 on mobile (portrait, phone-like) · 16:9 on tablet+ (desktop)
function SnapchatCallView({ threadId, videoRoom, filter, filters, remoteName, remoteAvatar, onEnd, onFilterChange }) {
  const [micOn, setMicOn]   = useState(true);
  const [camOn, setCamOn]   = useState(true);
  const [ringing, setRinging] = useState(true);
  // swapped: false = big shows the other person / mini shows you (default).
  // true = big shows you / mini shows the other person. Toggled via the swap
  // button on the mini tile or by tapping the mini tile itself.
  const [swapped, setSwapped] = useState(false);
  const [tok, setTok] = useState(null);
  const [url, setUrl] = useState(null);
  const [lkErr, setLkErr] = useState(null);
  const [perms, setPerms] = useState({ mic: false, cam: false, checking: false });
  const [showFilters, setShowFilters] = useState(false);
  const previewRef = useRef(null);
  const previewStreamRef = useRef(null);

  const requestPermissions = async () => {
    setPerms((p) => ({ ...p, checking: true }));
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      s.getTracks().forEach((t) => t.stop());
      setPerms({ mic: true, cam: true, checking: false });
      toast.success("Mic + camera ready");
    } catch {
      setPerms({ mic: false, cam: false, checking: false });
      toast.error("Browser denied mic/camera. Check site permissions and retry.");
    }
  };

  useEffect(() => {
    let cancel = false;
    api.post("/livekit/token", { stream_id: `dm-${threadId}`, role: "cohost", room_override: videoRoom })
      .then(({ data }) => { if (!cancel) { setTok(data.token); setUrl(data.url); } })
      .catch((e) => !cancel && setLkErr(e?.response?.data?.detail || "Call unavailable"));
    return () => { cancel = true; };
  }, [threadId, videoRoom]);

  // Local self-view is populated by LocalCameraAttacher (below) which attaches
  // LiveKit's local camera video track directly to the PiP <video> element.
  // This avoids calling getUserMedia twice (which would lock the device and
  // prevent LiveKit from publishing) — one capture, shared between PiP + SFU.

  // Toggle camera on/off — LocalTrackToggler handles the actual LiveKit publish/unpublish.

  return (
    <div className="dm-call-filter relative bg-black rounded-2xl overflow-hidden mx-auto w-full aspect-[9/16] sm:aspect-video max-w-md sm:max-w-3xl" data-testid="dm-call-overlay">
      <style>{`.dm-call-filter video { filter: ${filters.find((f) => f.id === filter)?.css || "none"}; transition: filter 200ms ease; }`}</style>

      {/* Pre-flight: mic + camera permissions must be granted BEFORE LiveKit
          tries to publish, otherwise the room mounts silently muted with no
          video. Runs the same getUserMedia probe as the old Studio card. */}
      {!(perms.mic && perms.cam) && (
        <div className="absolute inset-0 z-50 bg-black/90 backdrop-blur flex items-center justify-center p-6" data-testid="call-permissions-card">
          <div className="max-w-sm w-full glass p-5 space-y-3 border-l-2 border-nexus-cyan">
            <div className="label-eyebrow text-nexus-cyan flex items-center gap-2"><Mic className="w-3.5 h-3.5" /><Video className="w-3.5 h-3.5" />// call needs mic + camera</div>
            <p className="text-[11px] text-white/60 leading-relaxed">Your browser will prompt for microphone and camera access. <span className="text-nexus-cyan">Tap Allow</span> — without it your caller will see a black screen and hear silence.</p>
            <div className="flex items-center gap-2 text-[11px]">
              <span className={`inline-flex items-center gap-1.5 px-2 py-1 border ${perms.mic ? "border-nexus-mint/60 text-nexus-mint" : "border-white/10 text-white/40"}`} data-testid="call-perm-mic">
                <Mic className="w-3 h-3" /> {perms.mic ? "Mic granted" : "Mic pending"}
              </span>
              <span className={`inline-flex items-center gap-1.5 px-2 py-1 border ${perms.cam ? "border-nexus-mint/60 text-nexus-mint" : "border-white/10 text-white/40"}`} data-testid="call-perm-cam">
                <Video className="w-3 h-3" /> {perms.cam ? "Camera granted" : "Camera pending"}
              </span>
            </div>
            <button onClick={requestPermissions} disabled={perms.checking} className="btn-nexus w-full justify-center" data-testid="call-perm-request">
              {perms.checking ? "Checking…" : "Grant camera + microphone access"}
            </button>
          </div>
        </div>
      )}

      {/* REMOTE box — full-frame by default, shrinks to the mini corner tile when swapped */}
      <div
        className={swapped
          ? "absolute top-3 right-3 w-24 h-32 sm:w-28 sm:h-36 rounded-xl overflow-hidden border-2 border-white/60 shadow-2xl z-40 cursor-pointer"
          : "absolute inset-0 z-10"}
        onClick={() => swapped && setSwapped(false)}
        data-testid="dm-call-remote-box"
      >
        {lkErr && <div className="w-full h-full flex items-center justify-center text-white/40 text-xs">Call: {lkErr}</div>}
        {!lkErr && (!tok || !url) && <div className="w-full h-full flex items-center justify-center text-white/40 text-xs">Connecting live…</div>}
        {!lkErr && tok && url && (
          <LiveKitRoom
            token={tok}
            serverUrl={url}
            connect
            audio={true}
            video={true}
            data-lk-theme="default"
            style={{ height: "100%", width: "100%", background: "black" }}
          >
            <RemoteOnlyStage />
            <RemoteJoinWatcher onRemoteJoined={() => setRinging(false)} />
            <RemoteLeftWatcher onRemoteLeft={() => { setRinging(false); onEnd(); }} />
            <RingTone active={ringing} />
            <LocalTrackToggler micOn={micOn} camOn={camOn} />
            <LocalCameraAttacher previewRef={previewRef} />
            <RoomAudioRenderer />
            <CallDiagnostics url={url} tok={tok} />
          </LiveKitRoom>
        )}
        {swapped && <div className="absolute bottom-1 left-1 text-[9px] tracking-widest uppercase text-white/80 bg-black/40 px-1.5 rounded z-40">{remoteName || "Them"}</div>}
      </div>

      {/* LOCAL box — mini corner tile by default (always shows YOU via getUserMedia), full-frame when swapped */}
      <div
        className={swapped
          ? "absolute inset-0 z-10"
          : "absolute top-3 right-3 w-24 h-32 sm:w-28 sm:h-36 rounded-xl overflow-hidden border-2 border-white/60 shadow-2xl z-40 cursor-pointer"}
        onClick={() => !swapped && setSwapped(true)}
        data-testid="dm-call-pip"
      >
        <video ref={previewRef} autoPlay muted playsInline className="w-full h-full object-cover scale-x-[-1]" />
        {!camOn && (
          <div className="absolute inset-0 bg-black flex items-center justify-center text-white/70 text-[10px] font-display tracking-widest">CAM OFF</div>
        )}
        {!swapped && <div className="absolute bottom-1 left-1 text-[9px] tracking-widest uppercase text-white/80 bg-black/40 px-1.5 rounded">You</div>}
      </div>

      {/* Swap button — sits on the mini tile’s corner regardless of which box is currently mini */}
      {/* Fixed position — the mini tile itself is always top-3/right-3 w-24/w-28
          regardless of which feed is currently mini, so this sits just left of it. */}
      <button
        onClick={(e) => { e.stopPropagation(); setSwapped((v) => !v); }}
        className="absolute z-50 top-3 right-[7.25rem] sm:right-[8.5rem] p-1.5 rounded-full bg-black/60 backdrop-blur border border-white/30 text-white hover:bg-black/80 transition-colors"
        data-testid="dm-call-swap-pip"
        title="Swap views"
      >
        <RefreshCw className="w-3.5 h-3.5" />
      </button>

      {/* Remote name pill top-left */}
      <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/50 backdrop-blur px-2.5 py-1 rounded-full z-40" data-testid="dm-call-remote-name">
        {remoteAvatar && <img src={remoteAvatar} alt="" className="w-5 h-5 rounded-full" />}
        <span className="text-white text-[11px] font-display font-bold truncate max-w-[10rem]">{remoteName}</span>
      </div>

      {/* Ringing indicator (continuous while nobody has answered) */}
      {ringing && (
        <div className="absolute top-16 sm:top-14 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/60 backdrop-blur px-3 py-1.5 rounded-full z-40 animate-pulse" data-testid="dm-call-ringing">
          <Phone className="w-3.5 h-3.5 text-green-400 animate-pulse" />
          <span className="text-white text-[11px] tracking-widest uppercase font-display">Ringing…</span>
        </div>
      )}

      {/* Effects toggle — labeled (icon + text), not a bare icon, so it reads
          as a feature rather than a stray/unexplained element. */}
      <button
        onClick={() => setShowFilters((v) => !v)}
        title="Video effects"
        className="absolute top-3 sm:top-14 right-32 sm:right-3 sm:top-auto sm:bottom-24 flex items-center gap-1.5 px-3 py-2 rounded-full bg-black/35 backdrop-blur text-white hover:bg-black/55 transition-colors z-40"
        data-testid="dm-call-filters-toggle"
      >
        <Sparkles className="w-4 h-4" />
        <span className="text-[10px] font-display tracking-wider uppercase">Effects</span>
      </button>

      {/* Filter carousel */}
      {showFilters && (
        <div className="absolute left-3 right-3 bottom-24 flex gap-1.5 overflow-x-auto z-40 pb-1" data-testid="dm-filter-bar">
          {filters.map((f) => (
            <button
              key={f.id}
              onClick={() => onFilterChange(f.id)}
              data-testid={`dm-filter-${f.id}`}
              className={`shrink-0 px-3 py-1 rounded-full text-[10px] font-display tracking-wider backdrop-blur border ${
                filter === f.id ? "bg-white text-black border-white" : "bg-black/40 text-white border-white/30 hover:border-white/60"
              }`}
            >{f.label}</button>
          ))}
        </div>
      )}

      {/* Transparent bottom overlay: mic, cam, end-call */}
      <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-4 z-40" data-testid="dm-call-controls">
        <button
          onClick={() => setMicOn((v) => !v)}
          title={micOn ? "Mute mic" : "Unmute mic"}
          data-testid="dm-call-mic-toggle"
          className={`w-12 h-12 rounded-full flex items-center justify-center backdrop-blur transition-colors ${micOn ? "bg-white/25 hover:bg-white/40 text-white" : "bg-red-500/80 hover:bg-red-500 text-white"}`}
        >
          {micOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
        </button>
        <button
          onClick={() => setCamOn((v) => !v)}
          title={camOn ? "Turn off camera" : "Turn on camera"}
          data-testid="dm-call-cam-toggle"
          className={`w-12 h-12 rounded-full flex items-center justify-center backdrop-blur transition-colors ${camOn ? "bg-white/25 hover:bg-white/40 text-white" : "bg-red-500/80 hover:bg-red-500 text-white"}`}
        >
          {camOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
        </button>
        <button
          onClick={() => { setRinging(false); onEnd(); }}
          title="End call"
          data-testid="dm-call-end"
          className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-2xl transition-transform hover:scale-105"
        >
          <PhoneOff className="w-6 h-6" />
        </button>
      </div>
    </div>
  );
}

// RemoteJoinWatcher — lives inside <LiveKitRoom> so it can call useRemoteParticipants().
// Fires onRemoteJoined() the moment the OTHER person joins the room. This is
// what stops the ringing tone — not the caller's own connection, so ringing
// keeps pulsing until the receiver actually picks up.
function RemoteJoinWatcher({ onRemoteJoined }) {
  const remotes = useRemoteParticipants();
  useEffect(() => { if (remotes.length > 0) onRemoteJoined(); }, [remotes.length, onRemoteJoined]);
  return null;
}

// RemoteLeftWatcher — mirror of RemoteJoinWatcher. Uses the same
// useRemoteParticipants() hook (which under the hood re-renders on LiveKit's
// ParticipantConnected / ParticipantDisconnected room events) so the moment the
// other side hangs up (drops their participant), this side calls onEnd() and
// tears its own call view down. A short grace timer swallows transient
// reconnects (network blip / camera-switch on mobile) so we don't kill a call
// that's about to recover in ~1 second.
function RemoteLeftWatcher({ onRemoteLeft }) {
  const remotes = useRemoteParticipants();
  const hadRemoteRef = useRef(false);
  useEffect(() => {
    if (remotes.length > 0) {
      hadRemoteRef.current = true;
      return;
    }
    // Only fire if a remote WAS present and now isn't — otherwise this fires
    // on initial mount before anyone joined (we already handle that via
    // the ringing state + the caller's own timeout).
    if (!hadRemoteRef.current) return;
    const t = setTimeout(() => { onRemoteLeft?.(); }, 1500);
    return () => clearTimeout(t);
  }, [remotes.length, onRemoteLeft]);
  return null;
}

// LocalTrackToggler — lives inside <LiveKitRoom> so it can grab the local
// participant via useLocalParticipant(). LiveKitRoom itself is mounted with
// audio={true} video={true}, so it already auto-publishes both at connect —
// this component only needs to react to actual mute/camera-off TOGGLES from
// here on. publishedRef starts pre-seeded with the initial micOn/camOn so we
// don't redundantly re-call setMicrophoneEnabled/setCameraEnabled the instant
// this mounts (that duplicate call, racing the room's own auto-publish, was
// a likely source of intermittent broken call audio). If publishing the
// camera fails on a real toggle (common when the pre-flight probe just
// released the device on mobile Safari), we retry once after 1500ms and
// surface a toast on the second failure so the user knows their video isn't
// reaching the other side.
function LocalTrackToggler({ micOn, camOn }) {
  const { localParticipant } = useLocalParticipant();
  const publishedRef = useRef({ mic: micOn, cam: camOn });  // last-applied value per source

  useEffect(() => {
    if (!localParticipant) return;
    let cancelled = false;

    // Microphone — never as fragile as camera on transient errors, so a single
    // attempt is enough. Log to console but don't nag the user.
    (async () => {
      if (publishedRef.current.mic === micOn) return;
      try {
        await localParticipant.setMicrophoneEnabled(micOn);
        publishedRef.current.mic = micOn;
      } catch (e) {
        console.warn("[NEXUS call] setMicrophoneEnabled failed:", e?.message || e);
      }
    })();

    // Camera — the critical one. Retry once if the first publish throws
    // (device-busy right after the permission probe stopped its tracks).
    (async () => {
      if (publishedRef.current.cam === camOn) return;
      const tryPublish = async () => {
        await localParticipant.setCameraEnabled(camOn);
      };
      try {
        await tryPublish();
        if (!cancelled) publishedRef.current.cam = camOn;
      } catch (e1) {
        console.warn("[NEXUS call] setCameraEnabled attempt 1 failed:", e1?.message || e1);
        // Transient-error retry after the browser has had a moment to release
        // the device. Only retry when we were trying to TURN ON the camera —
        // turning it off never legitimately fails.
        if (!camOn) return;
        await new Promise((r) => setTimeout(r, 1500));
        if (cancelled || !localParticipant) return;
        try {
          await tryPublish();
          publishedRef.current.cam = camOn;
          console.info("[NEXUS call] setCameraEnabled retry succeeded");
        } catch (e2) {
          console.error("[NEXUS call] setCameraEnabled retry failed:", e2?.message || e2);
          if (!cancelled) {
            toast.error("Camera couldn't publish. Check site permissions and reload.");
          }
        }
      }
    })();

    return () => { cancelled = true; };
  }, [localParticipant, micOn, camOn]);
  return null;
}

// LocalCameraAttacher — lives inside <LiveKitRoom>. Grabs the local
// participant's camera VideoTrack (published by LiveKitRoom's video={true}
// auto-publish + LocalTrackToggler's setCameraEnabled) and attaches that
// SAME track to the PiP <video> element in the parent. This means the
// browser only opens the camera device ONCE — that single MediaStreamTrack
// is fed to both the SFU (for the remote peer) and the local PiP (for the
// self-view). Fixes the "two consumers can't hold the same device" bug that
// prevented publishing on prod.
function LocalCameraAttacher({ previewRef }) {
  const { localParticipant } = useLocalParticipant();
  useEffect(() => {
    const videoEl = previewRef?.current;
    if (!videoEl || !localParticipant) return;

    let attachedTrack = null;
    const tryAttach = () => {
      if (attachedTrack) return;
      const pub = localParticipant.getTrackPublication(Track.Source.Camera);
      const track = pub?.videoTrack;
      if (track) {
        track.attach(videoEl);
        attachedTrack = track;
      }
    };

    // Immediate attempt in case the camera was already published before we mounted.
    tryAttach();

    // React to future publications (LiveKit publishes async after connect).
    const onPublished = () => tryAttach();
    const onUnpublished = () => {
      if (attachedTrack) { try { attachedTrack.detach(videoEl); } catch (_) { /* noop */ } attachedTrack = null; }
    };
    localParticipant.on("localTrackPublished", onPublished);
    localParticipant.on("localTrackUnpublished", onUnpublished);

    return () => {
      localParticipant.off("localTrackPublished", onPublished);
      localParticipant.off("localTrackUnpublished", onUnpublished);
      if (attachedTrack) { try { attachedTrack.detach(videoEl); } catch (_) { /* noop */ } }
    };
  }, [localParticipant, previewRef]);
  return null;
}


// RingTone — Web Audio dual-tone (440/480 Hz PSTN) that pulses continuously
// while `active`. No time cap — only stops when the parent flips `active` off
// (receiver joins, or user ends the call).
function RingTone({ active }) {
  useEffect(() => {
    if (!active) return;
    let stopped = false;
    // Shared, gesture-primed context (see lib/ringAudio) — a fresh context
    // created here has no guaranteed prior user gesture in its call stack
    // on some mobile browsers and can stay silently suspended.
    const ctx = getRingContext();
    if (!ctx) return;
    const gain = ctx.createGain();
    gain.gain.value = 0.35;
    gain.connect(ctx.destination);
    const ringOnce = () => {
      if (stopped) return;
      const o1 = ctx.createOscillator(); o1.frequency.value = 440; o1.connect(gain); o1.start(); o1.stop(ctx.currentTime + 1);
      const o2 = ctx.createOscillator(); o2.frequency.value = 480; o2.connect(gain); o2.start(); o2.stop(ctx.currentTime + 1);
    };
    ringOnce();
    const iv = setInterval(ringOnce, 3000); // ring every 3 s until stopped
    return () => { stopped = true; clearInterval(iv); };
  }, [active]);
  return null;
}

// RemoteOnlyStage — Big call view that renders ONLY remote participants' camera
// tracks (with placeholder while waiting). The local participant is EXCLUDED
// from this stage on EVERY device — the local feed lives exclusively in the
// mini PiP self-view up in the parent overlay. Big = other person, mini = you.
function RemoteOnlyStage() {
  // withPlaceholder ensures every remote participant appears here even before
  // they publish a camera track (we show their avatar placeholder tile).
  const allTracks = useTracks([{ source: Track.Source.Camera, withPlaceholder: true }]);
  const remoteTracks = allTracks.filter((tr) => tr.participant && tr.participant.isLocal === false);

  if (remoteTracks.length === 0) {
    // Nobody else on the call yet. Do NOT fall back to the local camera here —
    // that would duplicate the mini self-view and confuse both participants
    // ("why am I in the big window?"). Show a neutral waiting panel instead.
    return (
      <div className="w-full h-full relative bg-gradient-to-br from-nexus-panel to-black" data-testid="dm-call-waiting">
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur px-3 py-1.5 rounded-full text-white/80 text-[10px] tracking-widest uppercase font-display z-30">
          Waiting for them to join…
        </div>
      </div>
    );
  }
  return (
    <GridLayout tracks={remoteTracks} style={{ height: "100%" }}>
      <ParticipantTile />
    </GridLayout>
  );
}

// InstantSelfPreview kept for backwards-compat / any other caller
function InstantSelfPreview({ active }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    if (!active) return;
    let stream = null; let released = false;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
        if (released) { stream.getTracks().forEach((t) => t.stop()); return; }
        if (ref.current) { ref.current.srcObject = stream; const p = ref.current.play?.(); if (p) p.catch(() => {}); }
      } catch { setVisible(false); }
    })();
    const t = setTimeout(() => { released = true; stream?.getTracks().forEach((tr) => tr.stop()); setVisible(false); }, 2200);
    return () => { released = true; clearTimeout(t); stream?.getTracks().forEach((tr) => tr.stop()); };
  }, [active]);
  if (!visible) return null;
  return (
    <div className="absolute inset-0 z-30 bg-black flex items-center justify-center pointer-events-none" data-testid="dm-instant-preview">
      <video ref={ref} className="w-full h-full object-cover scale-x-[-1]" autoPlay muted playsInline />
      <div className="absolute bottom-3 left-3 bg-black/60 text-white text-[10px] tracking-widest uppercase font-display px-2 py-1 rounded">Connecting live…</div>
    </div>
  );
}

// ==================================================================
// CallDiagnostics — Visible ?debug=1 overlay for prod video-call issues.
// Renders NOTHING unless the URL query has ?debug=1. When it does, it
// displays a semi-transparent panel with every observable state from the
// LiveKit SDK: connection state, server URL, token first 20 chars, local
// publications (mic + cam) with track SIDs, list of remote participants
// and each of their published tracks. This is the single source of truth
// for diagnosing "remote video not appearing" on production.
// ==================================================================
function CallDiagnostics({ url, tok }) {
  const [params] = useSearchParams();
  const enabled = params.get("debug") === "1";
  const connectionState = useConnectionState();
  const { localParticipant } = useLocalParticipant();
  const remotes = useRemoteParticipants();

  // Ticker so the panel updates when publications change (LiveKit hooks are
  // event-driven but publication maps mutate in place).
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const iv = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(iv);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    console.info("[NEXUS diag] connectionState:", connectionState);
  }, [connectionState, enabled]);

  if (!enabled) return null;

  const localPubs = localParticipant ? Array.from(localParticipant.trackPublications?.values?.() || []) : [];
  const stateColor = {
    connected: "#00FF66",
    connecting: "#FFC107",
    reconnecting: "#FFC107",
    disconnected: "#FF3366",
    failed: "#FF3366",
  }[connectionState] || "#00F0FF";

  return (
    <div
      data-testid="call-diagnostics"
      className="absolute top-16 left-3 right-3 sm:right-auto sm:w-96 max-h-[65%] overflow-y-auto z-50 bg-black/90 backdrop-blur border border-nexus-cyan/40 rounded-lg p-3 text-[10px] font-mono text-white/90 space-y-2"
    >
      <div className="flex items-center justify-between border-b border-white/10 pb-1.5">
        <div className="font-display font-bold tracking-widest uppercase text-nexus-cyan">// call diagnostics</div>
        <div className="uppercase tracking-widest" style={{ color: stateColor }}>{connectionState}</div>
      </div>

      <div className="space-y-0.5">
        <Row label="server">{url ? new URL(url.replace("wss:", "https:").replace("ws:", "http:")).host : "—"}</Row>
        <Row label="scheme" color={url?.startsWith("wss:") ? "#00FF66" : "#FF3366"}>{url?.split("://")[0] || "—"}</Row>
        <Row label="token">{tok ? `${tok.slice(0, 22)}…(${tok.length})` : "—"}</Row>
        <Row label="local id">{localParticipant?.identity || "—"}</Row>
      </div>

      <div className="border-t border-white/10 pt-1.5">
        <div className="tracking-widest uppercase text-white/50 mb-1">local publications ({localPubs.length})</div>
        {localPubs.length === 0 && <div className="text-nexus-crimson">// none published — this is the bug</div>}
        {localPubs.map((pub) => (
          <div key={pub.trackSid || pub.trackName} className="pl-2 border-l border-white/15 mb-1" data-testid={`diag-local-pub-${pub.source}`}>
            <div>{pub.source} · {pub.trackSid || "no-sid"}</div>
            <div className="text-white/50">muted={String(!!pub.isMuted)} · subscribed={String(!!pub.isSubscribed)} · enabled={String(!!pub.track?.mediaStreamTrack?.enabled)}</div>
          </div>
        ))}
      </div>

      <div className="border-t border-white/10 pt-1.5">
        <div className="tracking-widest uppercase text-white/50 mb-1">remote participants ({remotes.length})</div>
        {remotes.length === 0 && <div className="text-nexus-amber">// nobody else connected — waiting for peer to join room</div>}
        {remotes.map((rp) => {
          const pubs = Array.from(rp.trackPublications?.values?.() || []);
          return (
            <div key={rp.identity} className="pl-2 border-l border-nexus-cyan/40 mb-1" data-testid={`diag-remote-${rp.identity}`}>
              <div className="text-nexus-cyan">{rp.identity} · {rp.name}</div>
              <div className="text-white/50">pubs={pubs.length} · state={rp.connectionQuality || "—"}</div>
              {pubs.map((pub) => (
                <div key={pub.trackSid} className="pl-2">
                  <span className={pub.isSubscribed ? "text-nexus-mint" : "text-nexus-crimson"}>
                    {pub.source} sid={pub.trackSid?.slice(0, 8)}… sub={String(pub.isSubscribed)} muted={String(pub.isMuted)}
                  </span>
                </div>
              ))}
              {pubs.length === 0 && <div className="text-nexus-crimson pl-2">// remote hasn&apos;t published anything</div>}
            </div>
          );
        })}
      </div>

      <div className="border-t border-white/10 pt-1.5 text-white/40 text-[9px]">
        Screenshot this panel + send to support. Green rows = healthy. Red rows = failure point.
      </div>
    </div>
  );
}

function Row({ label, color, children }) {
  return (
    <div className="flex gap-2">
      <span className="text-white/50 w-16 shrink-0">{label}</span>
      <span className="flex-1 break-all" style={color ? { color } : {}}>{children}</span>
    </div>
  );
}


